import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useRef, useState, type FormEvent, type ReactElement } from "react";
import { ConfirmDialog, ContentHeader, QueryStatus, StatusBadge, useHotkeys, useToast } from "@cx/ui";
import type { CheckoutFinishResult, CheckoutReadResult, CheckoutSessionView } from "@cx/contracts";
import { can, type Permissions } from "@cx/db";
import { formatDateBR, toQ4 } from "@cx/domain";
import { ApiFailure } from "../../api";
import { dateTime } from "../../format";
import { useSession } from "../../session";
import { CHECKOUT_STATE_LABEL, LINE_STATE_LABEL, checkoutError, lineState, progressPct, qty } from "./labels";
import { SOCKET_STATUS_LABEL, useCheckoutSocket, useThrottled } from "./useCheckoutSocket";

const LINE_TONE = { done: "success", partial: "warning", pending: "secondary" } as const;

/** Conferência de um pedido: bipar os itens, ver o progresso ao vivo e salvar (completo ou parcial). */
export function CheckoutSessionPage(): ReactElement {
  const { id } = useParams({ from: "/vendas/checkout/$id" });
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const canWrite = can(perms, "checkout", "create");
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [qtyText, setQtyText] = useState("1");
  const [confirm, setConfirm] = useState<"partial" | "reset" | "cancel" | null>(null);
  const [last, setLast] = useState<{ ok: boolean; text: string } | null>(null);
  const codeRef = useRef<HTMLInputElement>(null);
  const key = ["checkout", companyId, "session", id];
  const session = useQuery({
    queryKey: key,
    refetchOnMount: "always",
    queryFn: () => api<CheckoutSessionView>(`/checkout/sessions/${id}`),
  });
  // Conectado com o id da sessão: recebe as bipagens dela (de outro operador) e as mudanças de estado da empresa.
  const refreshSession = useThrottled(() => void qc.invalidateQueries({ queryKey: key }));
  const live = useCheckoutSocket(
    companyId,
    (e) => {
      if (e.sessionId === id) refreshSession();
    },
    id,
  );
  const refresh = () => qc.invalidateQueries({ queryKey: ["checkout", companyId] });
  const post = <T,>(path: string, body?: unknown) =>
    api<T>(`/checkout/sessions/${id}/${path}`, {
      method: "POST",
      headers: { "Idempotency-Key": crypto.randomUUID() },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  const focusScan = () => setTimeout(() => codeRef.current?.focus(), 0);
  const onError = (e: Error) => {
    toast("danger", checkoutError(e));
    refresh();
  };

  const read = useMutation({
    mutationFn: (v: { code: string; qtyQ4: number }) => post<CheckoutReadResult>("reads", v),
    onSuccess: (r) => {
      setLast({ ok: true, text: `${r.productName}: lido (${progressPct(r.progress)}% do pedido).` });
      setCode("");
      setQtyText("1");
      refresh();
      focusScan();
    },
    onError: (e) => {
      setLast({ ok: false, text: checkoutError(e) });
      codeRef.current?.select();
      if (e instanceof ApiFailure && (e.code === "order_changed" || e.code === "session_closed")) refresh();
    },
  });
  const finish = useMutation({
    mutationFn: (partial: boolean) => post<CheckoutFinishResult>("finish", { partial }),
    onSuccess: (r) => {
      setConfirm(null);
      refresh();
      if (r.outcome === "complete") {
        toast("success", "Checkout concluído");
        navigate({ to: "/vendas/checkout" });
      } else toast("success", "Checkout parcial salvo; a conferência continua aberta");
    },
    onError: (e) => {
      if (
        e instanceof ApiFailure &&
        e.code === "checkout_incomplete" &&
        session.data?.state === "open" &&
        confirm === null
      )
        setConfirm("partial");
      else onError(e);
    },
  });
  const reset = useMutation({
    mutationFn: () => post("reset"),
    onSuccess: () => {
      setConfirm(null);
      setLast(null);
      toast("success", "Conferência reiniciada");
      refresh();
      focusScan();
    },
    onError,
  });
  const cancel = useMutation({
    mutationFn: () => post("cancel"),
    onSuccess: () => {
      setConfirm(null);
      toast("success", "Conferência cancelada");
      refresh();
      navigate({ to: "/vendas/checkout" });
    },
    onError,
  });

  const v = session.data;
  const editable = canWrite && v?.state === "open";
  const busy = read.isPending || finish.isPending || reset.isPending || cancel.isPending;
  useHotkeys({ "alt+s": () => editable && !busy && finish.mutate(false) }, { allowInInputs: ["alt+s"] });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!code.trim() || busy) return;
    let qtyQ4: number;
    try {
      qtyQ4 = toQ4(qtyText);
    } catch {
      setLast({ ok: false, text: "Quantidade inválida." });
      return;
    }
    read.mutate({ code, qtyQ4 });
  };

  return (
    <>
      <ContentHeader
        title={v ? `Checkout do pedido nº ${v.order.number}` : "Checkout"}
        breadcrumb={["Vendas", "Checkout de pedidos", v ? `Pedido nº ${v.order.number}` : "…"]}
        actions={
          <Link to="/vendas/checkout" className="btn btn-outline-secondary">
            Voltar à fila
          </Link>
        }
      />
      <QueryStatus loading={session.isLoading} error={session.error} />
      {v && (
        <>
          <div className="box card mb-3">
            <div className="row g-3 p-3">
              <div className="col-6 col-md-3">
                <div className="small text-muted">Cliente</div>
                <div>{v.order.contactName}</div>
              </div>
              <div className="col-6 col-md-2">
                <div className="small text-muted">Data</div>
                <div>{formatDateBR(v.order.date)}</div>
              </div>
              <div className="col-6 col-md-2">
                <div className="small text-muted">Logística</div>
                <div>{v.order.carrierName ?? "—"}</div>
              </div>
              <div className="col-6 col-md-2">
                <div className="small text-muted">Situação do pedido</div>
                <StatusBadge tone={v.order.status.color}>{v.order.status.label}</StatusBadge>
              </div>
              <div className="col-12 col-md-3">
                <div className="small text-muted">Conferência</div>
                <div data-testid="session-state">
                  {CHECKOUT_STATE_LABEL[v.state]}
                  {v.partialAt && v.state === "open" && " (parcial salvo)"}
                </div>
                <div className="small text-muted" role="status" aria-live="polite">
                  {SOCKET_STATUS_LABEL[live]}
                </div>
              </div>
            </div>
            <div className="px-3 pb-3">
              <div
                className="progress"
                role="progressbar"
                aria-label="Progresso da conferência"
                aria-valuenow={progressPct(v.progress)}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div
                  className={`progress-bar${v.progress.complete ? " bg-success" : ""}`}
                  style={{ width: `${progressPct(v.progress)}%` }}
                />
              </div>
              <div className="small mt-1" data-testid="session-progress">
                {qty(v.progress.checkedQ4)} de {qty(v.progress.requiredQ4)} conferidos ·{" "}
                {v.progress.linesDone} de {v.progress.linesTotal} itens
              </div>
            </div>
          </div>

          {editable && (
            <div className="box card mb-3">
              <form className="row g-2 p-3 align-items-end" onSubmit={submit} aria-label="Bipar produto">
                <div className="col-12 col-md-6">
                  <label className="form-label small" htmlFor="ck-item">
                    Produto (GTIN, GTIN tributário ou SKU)
                  </label>
                  <input
                    id="ck-item"
                    ref={codeRef}
                    className="form-control form-control-lg"
                    autoComplete="off"
                    autoFocus
                    maxLength={60}
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                  />
                </div>
                <div className="col-6 col-md-2">
                  <label className="form-label small" htmlFor="ck-qty">
                    Quantidade
                  </label>
                  <input
                    id="ck-qty"
                    className="form-control form-control-lg"
                    inputMode="decimal"
                    autoComplete="off"
                    value={qtyText}
                    onChange={(e) => setQtyText(e.target.value)}
                  />
                </div>
                <div className="col-6 col-md-4">
                  <button type="submit" className="btn btn-primary btn-lg w-100" disabled={busy}>
                    Registrar
                  </button>
                </div>
              </form>
              {last && (
                <div
                  className={`alert ${last.ok ? "alert-success" : "alert-danger"} mx-3 mb-3 py-2`}
                  role={last.ok ? "status" : "alert"}
                  data-testid="last-read"
                >
                  {last.text}
                </div>
              )}
            </div>
          )}

          <div className="box card mb-3">
            <div className="table-responsive">
              <table className="table align-middle mb-0" aria-label="Itens a conferir">
                <thead>
                  <tr>
                    <th scope="col">Item</th>
                    <th scope="col">Produto</th>
                    <th scope="col">Código</th>
                    <th scope="col">Localização</th>
                    <th scope="col">Depósito</th>
                    {v.stockVisible && (
                      <th scope="col" className="text-end">
                        Estoque
                      </th>
                    )}
                    <th scope="col" className="text-end">
                      Pedida
                    </th>
                    <th scope="col" className="text-end">
                      Conferida
                    </th>
                    <th scope="col">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {v.lines.map((l) => {
                    const st = lineState(l);
                    return (
                      <tr key={l.id} className={st === "done" ? "table-success" : undefined}>
                        <td>{l.itemPosition}</td>
                        <td>
                          {l.name}
                          {l.kitName && <div className="small text-muted">Componente de {l.kitName}</div>}
                        </td>
                        <td className="small">{[l.gtin, l.sku].filter(Boolean).join(" · ") || "—"}</td>
                        <td>{l.location ?? "—"}</td>
                        <td>{l.warehouseName}</td>
                        {v.stockVisible && (
                          <td className="text-end">
                            {l.stockQ4 === null || l.stockQ4 === undefined ? "—" : qty(l.stockQ4)}
                          </td>
                        )}
                        <td className="text-end">
                          {qty(l.requiredQ4)} {l.unit}
                        </td>
                        <td className="text-end" data-testid={`checked-${l.id}`}>
                          {qty(l.checkedQ4)}
                        </td>
                        <td>
                          <StatusBadge tone={LINE_TONE[st]}>{LINE_STATE_LABEL[st]}</StatusBadge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {editable && (
            <div className="d-flex flex-wrap gap-2 mb-3">
              <button
                type="button"
                className="btn btn-success"
                disabled={busy}
                onClick={() => finish.mutate(false)}
              >
                Salvar
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                disabled={busy}
                onClick={() => setConfirm("reset")}
              >
                Reiniciar conferência
              </button>
              <button
                type="button"
                className="btn btn-outline-danger"
                disabled={busy}
                onClick={() => setConfirm("cancel")}
              >
                Cancelar conferência
              </button>
            </div>
          )}

          <div className="box card">
            <h2 className="h6 p-3 mb-0">Últimas leituras</h2>
            {v.reads.length === 0 ? (
              <p className="text-muted px-3 pb-3 mb-0">Nenhuma leitura ainda.</p>
            ) : (
              <ul className="list-group list-group-flush" aria-label="Últimas leituras">
                {v.reads.map((r) => (
                  <li key={r.id} className="list-group-item small">
                    {dateTime(r.createdAt)} ·{" "}
                    {r.kind === "reset"
                      ? "Conferência reiniciada"
                      : `${r.productName ?? r.code} × ${qty(r.qtyQ4)} (${r.code})`}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <ConfirmDialog
            open={confirm === "partial"}
            title="Salvar checkout parcial?"
            message="Nem todos os itens foram conferidos. O pedido vai para a situação de checkout parcial e a conferência continua aberta para terminar depois."
            confirmLabel="Salvar parcial"
            tone="primary"
            onConfirm={() => finish.mutate(true)}
            onCancel={() => {
              setConfirm(null);
              focusScan();
            }}
          />
          <ConfirmDialog
            open={confirm === "reset"}
            title="Reiniciar a conferência?"
            message="Todas as quantidades conferidas voltam a zero. As leituras continuam no histórico."
            confirmLabel="Reiniciar"
            onConfirm={() => reset.mutate()}
            onCancel={() => setConfirm(null)}
          />
          <ConfirmDialog
            open={confirm === "cancel"}
            title="Cancelar a conferência?"
            message="A situação do pedido não muda; uma nova conferência começa do zero."
            confirmLabel="Cancelar conferência"
            cancelLabel="Voltar"
            onConfirm={() => cancel.mutate()}
            onCancel={() => setConfirm(null)}
          />
        </>
      )}
    </>
  );
}
